import { Component, OnInit, Input, Output, EventEmitter, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormArray, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AgrimotorService } from '../agrimotor.service';
import { formatDate } from '@angular/common';
import { Router, ActivatedRoute } from "@angular/router";
import { QuoteService } from '../../quote.service';
import { environment } from '../../../../environments/environment';
import { LocalStorageService } from 'ngx-webstorage';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-ag-quotation',
  templateUrl: './ag-quotation.component.html',
  styleUrls: ['./ag-quotation.component.css', '../../quote.component.css']
})
export class AgQuotationComponent implements OnInit {
  frmFields: any;
  submitted = false;
  quote_type: number = this.route.snapshot.params.quote_type;
  isNewQuote: boolean = !this.route.snapshot.params.id;
  @Input() authDetails;
  @Input() staticData;
  @Input() selectedIndex;
  @Input() modelObj;
  @Input() apiModelObj;
  @Output() modelObjUpdated = new EventEmitter();
  referAndExit: boolean = false;
  isSaving: boolean = false;

  constructor(private fb: FormBuilder, private modalService: NgbModal, private as: AgrimotorService,
    private router: Router, private cdr: ChangeDetectorRef, private route: ActivatedRoute,
    private quoteService: QuoteService, private sessionSt: LocalStorageService, private _snackBar: MatSnackBar) { }

  isAdmin = false;
  isStaff = false;
  isBroker = false;
  ngOnInit(): void {
    if (this.sessionSt.retrieve('is_admin')) { this.isAdmin = true; }
    if (this.sessionSt.retrieve('is_staff')) { this.isStaff = true; }
    if (this.sessionSt.retrieve('is_broker')) { this.isBroker = true; }
    this.frmFields = this.fb.group({
      voluntary_excess: ['', Validators.required],
      peliwica_fee: [''],
      commission: [''],
      broker_admin_fee: ['0', Validators.required],
      discount: ['0', Validators.required],
      refer_reason: ['', Validators.required],
    });
  }

  parentModelObj;
  cover_period;
  reference;
  proposer_type;
  isReferred: boolean = false;
  btn_text: string = 'Save & Exit';
  attention_required_text: string = '';

  ngOnChanges(changes: SimpleChanges) {
    const application = this.as.applicationOf(this.apiModelObj);
    if (this.apiModelObj && application) {
      const meta = this.as.quoteMetaOf(this.apiModelObj);
      this.modelObj = application;
      this.modelObj.id = meta.id;
      this.modelObj.reference = meta.reference;
      this.modelObj.status = meta.status;
      this.modelObj.type = this.apiModelObj.type;
      this.modelObj.sequence_number = this.apiModelObj.sequence_number;
      this.modelObj.company_id = this.apiModelObj.company_id;
      if (this.apiModelObj.financial_details) {
        this.modelObj.broker_admin_fee = this.apiModelObj.financial_details.broker_admin_fee;
      }
      this.patchValues();
    }

    if (this.selectedIndex >= 3) {
      this.parentModelObj = this.modelObj;
      let inception_date = this.modelObj.inception_date;
      this.reference = this.modelObj.reference;
      if (application) {
        this.reference = this.as.quoteMetaOf(this.apiModelObj).reference;
        if (application.is_referred) { this.isReferred = true; }
      } else {
        this.reference = this.modelObj.reference || '';
        if (this.modelObj.is_referred) { this.isReferred = true; }
      }
      this.proposer_type = this.modelObj.proposer?.proposer_type;

      // formatDate throws on a missing or unparseable inception date. That used to abort
      // the rest of this block, so the premium was never calculated and the page sat on
      // a £0 quote with no compulsory excess. A cover period we cannot render is not a
      // reason to skip the quote.
      try {
        let maxDate = new Date(inception_date).setFullYear(new Date(inception_date).getFullYear() + 1);
        let newDate = new Date(new Date(maxDate).getTime() - (24 * 60 * 60 * 1000));
        this.cover_period = formatDate(inception_date, 'dd-MM-yyyy', 'en-US') + ' to ' + formatDate(newDate, 'dd-MM-yyyy', 'en-US');
      } catch (e) {
        this.cover_period = '';
      }

      this.getPeliwicaPremium();
      this.frmFields.get('broker_admin_fee').setValue(this.modelObj.broker_admin_fee);
      this.frmFields.get('commission').setValue(this.modelObj.commission ?? '');
    }
  }

  ngAfterViewChecked() { this.cdr.detectChanges(); }
  get f(): { [key: string]: AbstractControl } { return this.frmFields.controls; }

  patchValues() {
    this.frmFields.patchValue({
      voluntary_excess: this.modelObj.voluntary_excess,
      commission: this.modelObj.commission ?? '',
      broker_admin_fee: this.modelObj.broker_admin_fee,
      discount: (this.modelObj.discount > 0) ? this.modelObj.discount : 0
    });
  }

  helptext = '';
  helpTextPopup(content, text) {
    this.modalService.open(content, { centered: true });
    this.helptext = text;
  }

  ipt: number = 0;
  gross: number = 0;
  total_prem_inc_ipt: number = 0;
  total_amount: number = 0;
  vehiclePremiumRows: { reg: string, premium: number }[] = [];
  gross_premium: number = 0;
  // An object, not a JSON string. As a string every `premiums.x` read is `undefined`,
  // which is what rendered Compulsory Excess as a bare "£" while the block still showed
  // because a non-empty string is truthy for *ngIf.
  premiums: any = { "referred": null, "ipt": 0, "gross": 0, "total_amount": 0, "gross_premium": 0, "vehicle_premiums": {}, "vehicle_excesses": {}, "driver_excesses": [], "compulsory_excess": 0 };
  calErrors: any = [];
  hasErrors: boolean = false;

  // Vehicle driver references go out as a flat list of unique id strings, whether the
  // in-memory model is holding ids or whole driver objects.
  private toDriverIds(list: any): string[] {
    const ids = (list || [])
      .map((d: any) => (typeof d === 'string') ? d : d?.id)
      .filter((id: any) => !!id);
    return Array.from(new Set<string>(ids));
  }

  private preparePayload(source: any): any {
    const obj = JSON.parse(JSON.stringify(source));

    // rating_type enum is fleet | ncd | none — 'no_previous' is rejected with a 400.
    if (obj.rating_type === 'no_previous') { obj.rating_type = 'none'; }

    // micro_enterprise is a boolean in the API, but the form collects Yes/No.
    if (obj.proposer && typeof obj.proposer.micro_enterprise === 'string') {
      obj.proposer.micro_enterprise = obj.proposer.micro_enterprise === 'Yes';
    }

    // Conviction "months banned" is named `banned` in the API.
    const normaliseConvictions = (list: any[]) => (list || []).map((c: any) => {
      const mapped: any = { ...c };
      if (mapped.months_banned !== undefined) {
        mapped.banned = mapped.months_banned;
        delete mapped.months_banned;
      }
      return mapped;
    });
    if (obj.proposer && obj.proposer.convictions) {
      obj.proposer.convictions = normaliseConvictions(obj.proposer.convictions);
    }
    (obj.drivers || []).forEach((d: any) => {
      if (Array.isArray(d.convictions)) { d.convictions = normaliseConvictions(d.convictions); }
    });

    // Map proposer claims field names to what the API expects (idempotent)
    if (obj.proposer && obj.proposer.claims && obj.proposer.claims.length > 0) {
      obj.proposer.claims = obj.proposer.claims.map((c: any) => {
        const mapped: any = { ...c };
        if (c.incident_type !== undefined) {
          mapped.type = c.incident_type;
          delete mapped.incident_type;
        }
        if (c.at_fault !== undefined) {
          mapped.fault = c.at_fault === 'Yes';
          delete mapped.at_fault;
        }
        if (c.is_open !== undefined) {
          mapped.open = c.is_open === 'Yes';
          delete mapped.is_open;
        }
        return mapped;
      });
    }

    // Fix vehicle mileage: cars & commercial → numeric; agricultural & trailers → remove
    if (obj.vehicles && obj.vehicles.length > 0) {
      obj.vehicles.forEach((v: any) => {
        if (v.type === 'cars' || v.type === 'commercial') {
          v.mileage = Number(v.mileage) || 0;
        } else if (v.type === 'agricultural' || v.type === 'trailer' || v.type === 'trailers') {
          delete v.mileage;
        }
        // horsebox: keep string mileage as-is

        // drivers / extended_drivers must be arrays of driver-id STRINGS. Objects here
        // make the agri-api return a 500 rather than a validation error, which is what
        // left the quotation page showing a £0 premium and a dead submit button.
        v.drivers = this.toDriverIds(v.drivers);
        v.extended_drivers = this.toDriverIds(v.extended_drivers);

        // mods must be a list of non-empty option strings
        v.mods = (v.mods || []).filter((m: any) => !!m);

        // Every vehicle needs a registration_number; VIN-identified vehicles (trailers,
        // non-road-registered agricultural) supply it as their identification number.
        if (!v.registration_number && v.vin) { v.registration_number = v.vin; }

        // The UI-only Yes/No helpers (is_road_registered, is_kept_overnight, ...) are
        // left in place on purpose: the API ignores unknown properties, and our own
        // backend stores them so the radios repopulate when the quote is reopened.
      });
    }

    // Map year_established to year for company proposer type
    if (obj.proposer && obj.proposer.proposer_type === 'company' && obj.proposer.year_established !== undefined) {
      obj.proposer.year = obj.proposer.year_established;
      delete obj.proposer.year_established;
    }

    // Ensure contracting_percentage and business_use_declaration are at root level
    if (obj.proposer) {
      if (obj.proposer.contracting_percentage && !obj.contracting_percentage) {
        obj.contracting_percentage = obj.proposer.contracting_percentage;
      }
      if (obj.proposer.business_use_declaration !== undefined && obj.business_use_declaration === undefined) {
        obj.business_use_declaration = obj.proposer.business_use_declaration;
      }
    }

    // Remove empty convictions (when has_convictions is "No")
    if (obj.proposer && obj.proposer.convictions) {
      obj.proposer.convictions = obj.proposer.convictions.filter((c: any) => c.code && c.date);
    }
    // Remove empty claims (when has_claims is "No")
    if (obj.proposer && obj.proposer.claims) {
      obj.proposer.claims = obj.proposer.claims.filter((c: any) => c.date && (c.type || c.incident_type));
    }

    // Filter and format driver claims
    if (obj.drivers && obj.drivers.length > 0) {
      obj.drivers.forEach((driver: any) => {
        // The drivers form writes '' into these when a driver has none; the API
        // expects arrays.
        if (!Array.isArray(driver.claims)) { driver.claims = []; }
        if (!Array.isArray(driver.convictions)) { driver.convictions = []; }
        if (driver.claims && Array.isArray(driver.claims)) {
          driver.claims = driver.claims.filter((c: any) => c.date && c.type);
          driver.claims.forEach((c: any) => {
            if (c.date instanceof Date) {
              c.date = formatDate(c.date, 'yyyy-MM-dd', 'en-US');
            } else if (typeof c.date === 'string' && c.date.includes('T')) {
              c.date = c.date.split('T')[0];
            }
          });
        }
      });
    }

    // Remove empty fleet experiences
    if (obj.fleet_experiences) {
      obj.fleet_experiences = obj.fleet_experiences.filter((f: any) => f.start_date);
    }

    return obj;
  }

  calculatePremium() {
    this.calErrors = [];
    this.hasErrors = false;
    this.bindPostData();
    let obj = this.preparePayload(this.modelObj);
    if (this.apiModelObj && this.apiModelObj.type != 3) {
      obj.policy_id = '';
      obj.effective_date = '';
    }
    if(obj.business_use_declaration ==null){
      obj.business_use_declaration = false;
    }
    // £0 is a valid answer and so is "not chosen yet" — the agri-api prices both the
    // same way and still returns the compulsory excess. Normalising to 0 here means a
    // premium is quoted as soon as the tab opens, rather than only once the broker picks
    // a non-zero excess. (Saving still requires an explicit selection.)
    const voluntary_excess = this.f.voluntary_excess.value;
    obj.voluntary_excess = (voluntary_excess === '' || voluntary_excess === null || voluntary_excess === undefined)
      ? 0 : voluntary_excess;
    if (obj.commission === null || obj.commission === undefined) { delete obj.commission; }
    let data = { data: { "type": "agrimotor-api/quote", "attributes": obj } };
    this.as.calculatePremium(data, this.authDetails)
      .subscribe(
        (res: any) => {
          if (res.meta) {
            this.hasErrors = false;
            this.isReferred = res.meta.referred;
            if (res.meta.referred) {
              this.calErrors = [];
              let msg = { "detail": "Your quote has referred, clicking save and exit will send your quote to the underwriter for review" };
              this.calErrors.push(msg);
              this.btn_text = "Save & Refer to Underwriter";
              this.attention_required_text = "This quote needs to be referred, please click 'Save & Refer to Underwriter' to proceed.";
            } else {
              this.calErrors = [];
              this.btn_text = "Save & Exit";
              this.attention_required_text = "";
            }
            // Every vehicle on the quote gets a line — not just the first one. On a
            // referral the API sends an empty array here rather than a keyed object,
            // so only treat a plain object as a premium breakdown.
            const premiumsByReg = res.meta.vehicle_premiums;
            this.vehiclePremiumRows = (premiumsByReg && !Array.isArray(premiumsByReg))
              ? Object.keys(premiumsByReg).map(reg => ({ reg: reg, premium: Number(premiumsByReg[reg]) }))
              : [];
            this.gross_premium = Number(res.meta.gross) || 0;
            this.total_prem_inc_ipt = Number(res.meta.gross) || 0;
            let broker_admin_fee = (this.modelObj) ? this.modelObj.broker_admin_fee : 0;
            this.frmFields.get('broker_admin_fee').setValue(broker_admin_fee);
            if (this.modelObj && this.modelObj.is_send_email == 1) {
              this.total_amount = this.gross_premium + Number(this.peliwica_fee) + Number(broker_admin_fee);
            } else {
              this.total_amount = this.gross_premium + Number(this.peliwica_fee);
            }
            this.premiums = res.meta;
          }
        },
        err => {
          // A failed calculate is NOT the same thing as a referral. Previously every
          // error was reported as "your quote has referred" while hasErrors silently
          // disabled the submit button, so the user was left with a £0 premium and a
          // button that did nothing. Show what the API actually said instead.
          this.calErrors = this.extractApiErrors(err);
          this.hasErrors = true;
          this.isReferred = false;
          this.btn_text = "Save & Exit";
          this.attention_required_text = "";
          this.vehiclePremiumRows = [];
          this.gross_premium = 0;
          this.total_prem_inc_ipt = 0;
          this.total_amount = 0;
          this.premiums = { "referred": null, "ipt": 0, "gross": 0, "total_amount": 0, "gross_premium": 0, "vehicle_premiums": {}, "vehicle_excesses": {}, "driver_excesses": [], "compulsory_excess": 0 };
        });
  }

  // The agri-api returns JSON:API errors: [{ code, title, detail, source: { pointer } }].
  // Turn them into something a broker can act on rather than dropping them.
  private extractApiErrors(err: any): any[] {
    const errors = err?.error?.errors;
    if (Array.isArray(errors) && errors.length > 0) {
      return errors.map((e: any) => {
        const field = this.humanisePointer(e?.source?.pointer);
        const text = e?.detail || e?.title || 'Invalid data';
        return { detail: field ? (field + ': ' + text) : text };
      });
    }
    return [{
      detail: 'The premium could not be calculated. Please check the quote details and try again'
              + (err?.status ? ' (error ' + err.status + ').' : '.')
    }];
  }

  private humanisePointer(pointer: string): string {
    if (!pointer) { return ''; }
    return pointer
      .replace('/data/attributes/', '')
      .split('/')
      .filter(p => p !== '')
      .map(p => /^\d+$/.test(p) ? '#' + (Number(p) + 1) : p.replace(/_/g, ' '))
      .join(' ')
      .replace(/^\w/, c => c.toUpperCase());
  }

  peliwica_fee: number = 0;
  getPeliwicaPremium() {
    // The commission lookup goes through SharedService.getAPI(), which maps every HTTP
    // failure to EMPTY — so on a 404 (no commission configured for this company against
    // the Agri Motor product) neither the next nor the error callback ever runs. Calling
    // calculatePremium() from there meant a missing fee config silently suppressed the
    // whole quote. Calculate from the terminal handlers instead: exactly one of complete
    // or error fires in every case, so the premium is always requested.
    this.quoteService.getLiveStockBrokerCommission(this.modelObj.company_id, environment.AGRIMOTOR_ID)
      .subscribe(
        (response: any) => {
          if (response && response.success) {
            if (this.modelObj.type == 3) {
              this.peliwica_fee = 0;
              this.frmFields.get('peliwica_fee').setValue(0);
            } else {
              this.peliwica_fee = response.result.uw_fee;
              this.frmFields.get('peliwica_fee').setValue(response.result.uw_fee);
            }
          }
        },
        (error) => { this.calculatePremium(); },
        () => { this.calculatePremium(); }
      );
  }

  onCommissionChange(event) {
    const max = this.modelObj?.broker_commission ?? 20;
    if (Number(event.target.value) > max) { this.frmFields.get('commission').setValue(max); }
    this.calculatePremium();
  }

  onBrokerCommissionChange(event) {
    this.modelObj.broker_admin_fee = event.target.value;
    this.calculatePremium();
  }

  onDiscountChange(event) {
    if (Number(event.target.value) > 10) { this.frmFields.get('discount').setValue(10); }
    if (Number(event.target.value) < 0) { this.frmFields.get('discount').setValue(0); }
    this.calculatePremium();
  }

  onReferReasonChange(value: string): void {
    this.frmFields.get('refer_reason').disable();
    if (value != '') { this.frmFields.get('refer_reason').enable(); }
  }

  setReferAndExit(val: boolean): void { this.referAndExit = val; }

  onSubmit(content, referral) {
    this.submitted = true;
    this.frmFields.get('refer_reason').disable();
    if (!this.frmFields.valid) { return false; }
    if (this.hasErrors) { return false; }
    if (this.modelObj.type == 2) { this.modelObj.policy_id = null; this.modelObj.effective_date = null; }
    if (this.isReferred) { this.modalService.open(referral, { centered: true }); } else { this.saveQuote(content); }
  }

  bindPostData() {
    let formData = this.frmFields.value;
    this.modelObj.voluntary_excess = formData.voluntary_excess;
    this.modelObj.commission = (formData.commission !== '' && formData.commission !== null) ? Number(formData.commission) : null;
    this.modelObj.discount = (formData.discount > 0) ? formData.discount : 0;
    this.modelObj.peliwica_fee = formData.peliwica_fee;
  }

  saveQuote(content) {
    this.isSaving = true;
    let formData = this.frmFields.value;
    let obj = this.preparePayload(this.modelObj);
    obj.voluntary_excess = formData.voluntary_excess;
    if (formData.commission !== '' && formData.commission !== null) {
      obj.commission = Number(formData.commission);
    } else {
      delete obj.commission;
    }
    obj.broker_admin_fee = formData.broker_admin_fee;
    obj.discount = formData.discount;
    obj.peliwica_fee = formData.peliwica_fee;
    // Keep the proposer's actual answer — this used to be hardcoded to false on save,
    // so the saved quote disagreed with the premium that was quoted.
    if (obj.business_use_declaration == null) { obj.business_use_declaration = false; }

    let refer_reason = '';
    if (this.isAdmin || this.isStaff) {
      if (this.referAndExit) {
        this.isReferred = true;
        refer_reason = this.frmFields.get('refer_reason').value;
        this.frmFields.get('refer_reason').disable();
        if (refer_reason == '') {
          this.frmFields.get('refer_reason').enable();
          this.isSaving = false;
          return;
        }
      }
    }

    let data = {
      data: {
        "type": this.modelObj.type,
        "attributes": obj,
        "id": this.modelObj.id,
        "sequence_number": this.modelObj.sequence_number,
        "broker_id": this.modelObj.broker_id,
        "old_policy_id": this.modelObj.old_policy_id,
      },
      meta: { "refer": this.isReferred, "refer_reason": refer_reason },
    };

    this.as.saveQuote(data)
      .subscribe(
        (res: any) => {
          if (res.success) {
            this.modalService.open(content, { centered: true });
            this.helptext = 'Data Saved.';
            this.modelObjUpdated.emit(this.modelObj);
            this.router.navigateByUrl("/agrimotor/quote-details/" + res.result.quote_data.quote_versions[0].agriculture_id);
          } else {
            this._snackBar.open(res.message, '', { duration: 5000, panelClass: 'text-danger' });
          }
          this.modalService.dismissAll();
          this.isSaving = false;
        },
        (err) => {
          this.isSaving = false;
          this._snackBar.open('Error saving quote. Please try again.', '', { duration: 5000, panelClass: 'text-danger' });
        });
  }
}
